import type { ReactElement } from "react"
import { useContext, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"

import WestIcon from "@mui/icons-material/West"
import { Grid } from "@mui/material"
import { Button, Tag, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BaseDrawer } from "../../components/drawers/BaseDrawer"
import { getTheme } from "../../hooks/UseTheme"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import { Deployment } from "../../types/generated/api/Deployment"
import type { KonanBasePredictionResponse } from "../../types/generated/api/KonanBasePredictionResponse"
import type { Model } from "../../types/generated/api/Model"
import type { WorkflowOutputResponse } from "../../types/generated/api/WorkflowOutputResponse"
import type { StandardErrorType } from "../../utils/ErrorHandling"
import { setCurrentModel } from "../Models"
import { WorkflowCanvasContext } from "../Workflows"
import { SMALL_ICON_SIZE } from "./Constants"
import { Context } from "./Context"
import type {
  DialogProps,
  ModelPredicitonsTestMutation,
  PredictionType,
  WorkflowPredictionsTestMutation,
} from "./Interfaces"
import { BulkCreatePredictions } from "./components/BulkCreatePredictions"
import { ComponentTesting } from "./components/ComponentTesting"
import { PredictionsTypeSelection } from "./components/PredictionsTypeSelection"
import { SinglePredictionCreation } from "./components/SinglePredictionCreation"

/**
 * PredictionCreationFlow Component
 *
 * A drawer component that manages the creation flow for predictions and Testing, supporting
 * single (manual) , bulk prediction creation modes and component testing mode.
 * The component implements a step-based interface where users can:
 * 1. Select the type of prediction/testing creation (manual/bulk/component)
 * 2. Complete the selected creation process
 *
 * Features:
 * - Supports manual (single) and bulk prediction creation
 * - Supports component testing
 * - Step-based navigation with back button functionality
 * - Displays workflow information and version
 * - Prevents closing while prediction creation is in progress
 *
 * @param isOpen - Controls the visibility of the drawer
 * @param onClose - Callback function to close the drawer
 */
export function PredictionCreationFlow(props: Readonly<DialogProps>): ReactElement {
  // Extract project ID from URL parameters

  const { isOpen, onClose, isTestModal } = props

  const dispatch = useDispatch()
  const theme = getTheme()

  // Access the current workflow context
  const { workflow } = useContext(WorkflowCanvasContext)
  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const name = currentProject?.category === Deployment.category.WORKFLOW ? workflow?.name : currentModel?.name

  const { t } = useTranslation("predictions", {
    keyPrefix: "predictionCreationFlow",
  })

  // State management
  const [type, setType] = useState<PredictionType>(null) // Tracks prediction creation type
  const [step, setStep] = useState<number>(0) // Tracks current step in the flow
  const [isSendingBulkRequests, setIsSendingBulkRequests] = useState<boolean>(false) // Loading state

  // Fetching and setting model to ensure that schema is present in later steps
  // TODO:: remove later when refactoring the setting of the current model
  useQuery<AxiosResponse<Model>, AxiosError>(
    ["model", currentModel?.uuid],
    () => KonanAPI.fetchModel(currentModel?.uuid as string),
    {
      onSuccess: ({ data }) => {
        dispatch(setCurrentModel(data))
      },
      enabled: !!currentModel?.uuid,
    },
  )

  /**
   * Mutation hook for sending prediction requests to the API
   */
  const sendPredictionMutation = useMutation<
    AxiosResponse<KonanBasePredictionResponse>,
    AxiosError,
    {
      projectId: string
      predictionBody?: Record<PropertyKey, unknown>
    }
  >({
    mutationFn: (props) => KonanAPI.sendPrediction(props.projectId, props.predictionBody),
  })

  /**
   * Mutation hook for sending model prediction test requests to the API
   */
  const sendModelPredictionTestMutation = useMutation<
    AxiosResponse<KonanBasePredictionResponse>,
    AxiosError<{ details: string }>,
    {
      modelId: string
      predictionBody: Record<PropertyKey, unknown>
    },
    ModelPredicitonsTestMutation
  >({
    mutationFn: ({ modelId, predictionBody }) => KonanAPI.sendModelPredictionTest(modelId, predictionBody),
  })

  /**
   * Mutation hook for sending workflowId prediction test requests to the API
   */
  const sendWorkFlowPredictionTestMutation = useMutation<
    AxiosResponse<WorkflowOutputResponse>,
    AxiosError<StandardErrorType>,
    {
      workflowId: string
      predictionBody: Record<PropertyKey, unknown>
    },
    WorkflowPredictionsTestMutation
  >({
    mutationFn: ({ workflowId, predictionBody }) => KonanAPI.sendWorkflowPredictionTest(workflowId, predictionBody),
  })

  const handleClose = (): void => {
    onClose()
    sendPredictionMutation.reset()
    sendModelPredictionTestMutation.reset()
    sendWorkFlowPredictionTestMutation.reset()

    // to avoid showing the user the changes in the UI while the drawer is transitioning to close
    setTimeout(() => {
      setType(null)
      setStep(0)
      setIsSendingBulkRequests(false)
    }, 1000)
  }

  function handlePredictionComponentRender(): JSX.Element | undefined {
    switch (type) {
      case "manual":
        return <SinglePredictionCreation />
      case "bulk":
        return <BulkCreatePredictions onClose={handleClose} />
      case "component":
        return <ComponentTesting />
    }
  }

  const steps = [
    { component: <PredictionsTypeSelection /> }, // Step 1: Type selection
    {
      component: handlePredictionComponentRender(),
    }, // Step 2: Single/Bulk prediction form
  ]

  const isPredictionsLoading =
    sendPredictionMutation.isLoading ||
    isSendingBulkRequests ||
    sendModelPredictionTestMutation.isLoading ||
    sendWorkFlowPredictionTestMutation.isLoading

  return (
    <BaseDrawer
      drawerProps={{
        open: isOpen,
        onClose: () => {
          // Prevent closing while prediction creation is in progress
          !isPredictionsLoading && handleClose()
        },
      }}
      title={isTestModal ? t("title.tests") : t("title.predictions")}
      onClose={() => {
        // Prevent closing while prediction creation is in progress
        !isPredictionsLoading && handleClose()
      }}
      isCloseButtonDisabled={isPredictionsLoading}
      subHeader={
        // Sub-header section with navigation and workflow info
        <Grid item container gap={1} alignItems={"center"}>
          {/* Back button - only shown after first step */}
          {step > 0 && (
            <Button
              size="small"
              onClick={() => {
                setStep((prev) => prev - 1)
                sendPredictionMutation.reset()
                sendModelPredictionTestMutation.reset()
                sendWorkFlowPredictionTestMutation.reset()
              }}
              isLoading={isPredictionsLoading}
              disabled={isPredictionsLoading}
            >
              <WestIcon style={{ color: theme.palette.primary.border.active, ...SMALL_ICON_SIZE }} />
            </Button>
          )}

          {/* Workflow name and version display */}
          <Typography variant="a" variantColor={2}>
            {name}
          </Typography>

          {currentProject?.category === Deployment.category.WORKFLOW && (
            <Tag color="green" size="small" style={{ textTransform: "lowercase" }}>
              v{workflow?.active_version.version}
            </Tag>
          )}
        </Grid>
      }
    >
      <Context.Provider
        value={{
          setStep,
          step,
          type,
          setType,
          sendPredictionMutation,
          isSendingBulkRequests,
          setIsSendingBulkRequests,
          sendWorkFlowPredictionTestMutation,
          sendModelPredictionTestMutation,
          isTestModal,
        }}
      >
        {/* Main content area - displays current step component */}
        {steps[step]?.component}
      </Context.Provider>
    </BaseDrawer>
  )
}
